import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/** Stock levels, the movement ledger and warehouses (spec §25, §26). */

export type StockMovementType =
  'PURCHASE' | 'SALE' | 'RETURN' | 'ADJUSTMENT' | 'RESERVATION' | 'RELEASE' | 'DAMAGE' | 'TRANSFER';

export interface StockRow {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  variantId: string | null;
  variantName: string | null;
  variantSku: string | null;
  warehouseId: string;
  warehouseName: string;
  warehouseCode: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  lowStockThreshold: number;
  isLowStock: boolean;
  updatedAt: string;
}

export interface StockMovementRow {
  id: string;
  type: StockMovementType;
  productId: string;
  productName: string;
  productSku: string;
  variantId: string | null;
  variantName: string | null;
  warehouseId: string;
  warehouseName: string;
  quantity: number;
  quantityBefore: number;
  quantityAfter: number;
  referenceType: string | null;
  referenceId: string | null;
  notes: string | null;
  performedBy: string | null;
  createdAt: string;
}

export interface InventorySummary {
  trackedRows: number;
  totalOnHand: number;
  totalReserved: number;
  totalAvailable: number;
  lowStockCount: number;
  outOfStockCount: number;
  untrackedProducts: number;
}

export interface Warehouse {
  id: string;
  name: string;
  code: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  country: string;
  pincode: string;
  managerName: string | null;
  managerPhone: string | null;
  isActive: boolean;
  isDefault: boolean;
  stockedProductCount: number;
  createdAt: string;
}

export interface StockListParams {
  page?: number;
  limit?: number;
  search?: string;
  warehouseId?: string;
  productId?: string;
  lowStock?: boolean;
  outOfStock?: boolean;
}

const keys = {
  all: ['admin', 'inventory'] as const,
  stock: (params: StockListParams) => [...keys.all, 'stock', params] as const,
  summary: (warehouseId?: string) => [...keys.all, 'summary', warehouseId ?? 'all'] as const,
  movements: (params: Record<string, unknown>) => [...keys.all, 'movements', params] as const,
  warehouses: () => ['admin', 'warehouses'] as const,
};

export function useStock(params: StockListParams) {
  return useQuery({
    queryKey: keys.stock(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<StockRow>>('/admin/inventory', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          warehouseId: params.warehouseId || undefined,
          productId: params.productId || undefined,
          // Sent only when on — `lowStock=false` means "no filter", not "healthy".
          lowStock: params.lowStock || undefined,
          outOfStock: params.outOfStock || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useInventorySummary(warehouseId?: string) {
  return useQuery({
    queryKey: keys.summary(warehouseId),
    queryFn: () =>
      apiClient.get<InventorySummary>('/admin/inventory/summary', {
        query: { warehouseId: warehouseId || undefined },
      }),
  });
}

export function useStockMovements(params: {
  productId?: string;
  warehouseId?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: keys.movements(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<StockMovementRow>>('/admin/inventory/movements', {
        query: {
          productId: params.productId || undefined,
          warehouseId: params.warehouseId || undefined,
          limit: params.limit ?? 20,
        },
      }),
  });
}

/** Any write that changes a level invalidates the stock list, the counters and the ledger. */
function useStockMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'catalog'] });
    },
  });
}

export function useAdjustStock() {
  return useStockMutation(
    (body: {
      productId: string;
      variantId?: string | null;
      warehouseId: string;
      mode: 'set' | 'delta';
      quantity: number;
      movementType?: StockMovementType;
      lowStockThreshold?: number;
      notes?: string;
    }) => apiClient.post<StockRow>('/admin/inventory/adjust', body),
  );
}

export function useTransferStock() {
  return useStockMutation(
    (body: {
      productId: string;
      variantId?: string | null;
      fromWarehouseId: string;
      toWarehouseId: string;
      quantity: number;
      notes?: string;
    }) => apiClient.post<StockRow[]>('/admin/inventory/transfer', body),
  );
}

// --- Warehouses --------------------------------------------------------------

export function useWarehouses() {
  return useQuery({
    queryKey: keys.warehouses(),
    queryFn: () => apiClient.get<Warehouse[]>('/admin/warehouses'),
  });
}

export function useSaveWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
      id
        ? apiClient.patch<Warehouse>(`/admin/warehouses/${id}`, body)
        : apiClient.post<Warehouse>('/admin/warehouses', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.warehouses() }),
  });
}

export function useSetDefaultWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.post<Warehouse>(`/admin/warehouses/${id}/default`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.warehouses() }),
  });
}

export function useDeleteWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/admin/warehouses/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.warehouses() });
      void queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
}
